import React from 'react';
import { formatPrice } from '../../utils/pricing';
import { EffectivePlanPricing, PricingCadence } from './promotionCode';
import {
  getDiscountPillLabel,
  getRedesignSublabel,
} from './redesignPresentation';

interface PlanTileRedesignProps {
  title: string;
  headerTag?: string | null;
  cadence: PricingCadence;
  pricing: EffectivePlanPricing;
  /** Campaign / promo-code name for the discount pill, when one applies. */
  discountName: string | null;
  /** Whole percent for the "X% less than monthly" pill; null hides it. */
  savingsVsMonthlyPercent: number | null;
  /** Gates the "$0 due today" fine print with the CTA copy. */
  showTrialFinePrint: boolean;
  /** Years of access a one-time purchase buys; drives the sublabel math. */
  accessYears?: number;
  cta: React.ReactNode;
}

// B-side plan tile of the pricing A/B test (ORA-392): price-forward layout —
// crossed-out base price over the discounted price, term sublabel, discount +
// savings pills, then the CTA with the trial fine print beneath it. Reuses the
// control tiles' card chrome (.pricing-option_inner_top_price) so both
// variants share background, borders, and CTA behavior.
const PlanTileRedesign: React.FC<PlanTileRedesignProps> = ({
  title,
  headerTag,
  cadence,
  pricing,
  discountName,
  savingsVsMonthlyPercent,
  showTrialFinePrint,
  accessYears = 1,
  cta,
}) => {
  const unitLabel =
    cadence === 'monthly' ? '/mo' : cadence === 'annual' ? '/yr' : 'once';

  const sublabel = getRedesignSublabel(pricing, cadence, accessYears);
  const discountPill = getDiscountPillLabel(pricing, discountName);

  return (
    <div className="glass-wrap relative plan-tile-redesign">
      <h2>
        {title}
        {headerTag && <span className="term-tag">{headerTag}</span>}
      </h2>
      <div className="pricing-option_inner_top_price">
        {pricing.isDiscountActive && (
          <div className="plan-tile-redesign_original">
            ${formatPrice(pricing.originalPrice)}
            {unitLabel === 'once' ? '' : unitLabel}
          </div>
        )}
        <div className="pricing-option_inner_top_price_amount">
          ${formatPrice(pricing.discountedPrice)}
          <span className="plan-tile-redesign_unit">{unitLabel}</span>
        </div>
        {sublabel && (
          <div className="plan-tile-redesign_sublabel">{sublabel}</div>
        )}
        {(discountPill || savingsVsMonthlyPercent != null) && (
          <div className="plan-tile-redesign_pills">
            {discountPill && (
              <span className="plan-pill plan-pill--discount">
                {discountPill}
              </span>
            )}
            {savingsVsMonthlyPercent != null && (
              <span className="plan-pill plan-pill--savings">
                {savingsVsMonthlyPercent}% less than monthly
              </span>
            )}
          </div>
        )}
        {cta}
        {showTrialFinePrint && (
          <div className="plan-tile-redesign_fineprint">$0 due today</div>
        )}
      </div>
    </div>
  );
};

export default PlanTileRedesign;
